/**
 * HR queue (`GET /api/tickets`) and HR edits (`PATCH /api/tickets/:key`): query params,
 * response shapes and the created-date presets the filter bar offers. The web app parses its URL
 * query string with the same schema, so a shared link means the same thing on both sides.
 */
import { z } from 'zod'
import { STAGES, type Stage } from '../config.ts'
import { personRefSchema, ticketDetailSchema, ticketSchema } from './api.ts'
import { parseIsoDate, toIsoDate, zonedParts } from './time.ts'

export const QUEUE_SORTS = ['key', 'priority', 'status', 'created', 'eta'] as const
export type QueueSort = (typeof QUEUE_SORTS)[number]
export type SortDir = 'asc' | 'desc'
export const QUEUE_PAGE_SIZE = 50
export const QUEUE_PAGE_SIZE_MAX = 200

/** Status filter value for a whole stage: `stage:in_progress`. */
export const STAGE_FILTER_PREFIX = 'stage:'
export const stageFilter = (stage: Stage) => `${STAGE_FILTER_PREFIX}${stage}`
/** The stage in a `stage:…` status filter, if it is one. */
export function stageOfFilter(value: string | undefined): Stage | undefined {
  if (!value?.startsWith(STAGE_FILTER_PREFIX)) return undefined
  const s = value.slice(STAGE_FILTER_PREFIX.length)
  return (STAGES as readonly string[]).includes(s) ? (s as Stage) : undefined
}

/** Assignee filter value for "Unassigned". */
export const UNASSIGNED_FILTER = 'none'

/** A new column sorts descending, except ETA (nearest first). DESIGN.md §3.1. */
export const defaultSortDir = (sort: QueueSort): SortDir => (sort === 'eta' ? 'asc' : 'desc')
export const DEFAULT_SORT: QueueSort = 'created'

const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v)
const optionalText = (max: number) =>
  z.preprocess(blankToUndefined, z.string().trim().max(max).optional())
const isoDate = z.preprocess(
  blankToUndefined,
  z
    .string()
    .refine((v) => parseIsoDate(v) !== null, 'Use YYYY-MM-DD')
    .optional(),
)

/** Most values one multi-value filter accepts (`?priority=high,urgent`). */
export const FILTER_VALUES_MAX = 50

/**
 * A multi-value filter: comma-separated (`a,b`), repeated (`?k=a&k=b`) or both. Blanks and
 * duplicates are dropped; nothing left means "any". Values within one filter combine with OR.
 */
const idList = (max: number) =>
  z.preprocess((v) => {
    const raw = Array.isArray(v) ? v : v === undefined || v === null ? [] : [v]
    const out = [
      ...new Set(
        raw
          .flatMap((x) => (typeof x === 'string' ? x.split(',') : [x]))
          .map((x) => (typeof x === 'string' ? x.trim() : x))
          .filter((x) => x !== ''),
      ),
    ]
    return out.length > 0 ? out : undefined
  }, z.array(z.string().max(max)).max(FILTER_VALUES_MAX).optional())

/**
 * `GET /api/tickets` query string. Every filter is optional; different filters combine with AND,
 * the values of one multi-value filter with OR ("is any of").
 */
export const queueQuerySchema = z
  .object({
    /** Matches the ticket key, title, description and the raiser's name. */
    q: optionalText(200),
    /** Inclusive org-time-zone days. */
    createdFrom: isoDate,
    createdTo: isoDate,
    /** Status ids. A legacy `stage:<stage>` value is moved to `stage`. */
    status: idList(80),
    /** Stages (status groups). Combined with `status` by AND: a ticket must match both. */
    stage: idList(80),
    category: idList(64),
    priority: idList(64),
    raisedBy: idList(64),
    /** Person ids and/or `none` (Unassigned). */
    assignee: idList(64),
    overdue: z.preprocess(
      (v) => v === '1' || v === 'true' || v === true,
      z.boolean().default(false),
    ),
    sort: z.preprocess(blankToUndefined, z.enum(QUEUE_SORTS).optional()),
    dir: z.preprocess(blankToUndefined, z.enum(['asc', 'desc']).optional()),
    page: z.preprocess(blankToUndefined, z.coerce.number().int().min(1).max(100_000).default(1)),
    pageSize: z.preprocess(
      blankToUndefined,
      z.coerce.number().int().min(1).max(QUEUE_PAGE_SIZE_MAX).default(QUEUE_PAGE_SIZE),
    ),
  })
  .transform((q, ctx) => {
    // Back-compat: `status=stage:in_progress` (the single status/stage select of Wave 4).
    const legacy = q.status?.filter((s) => s.startsWith(STAGE_FILTER_PREFIX)) ?? []
    const status = q.status?.filter((s) => !s.startsWith(STAGE_FILTER_PREFIX))
    const stageValues = [
      ...new Set([...(q.stage ?? []), ...legacy.map((s) => s.slice(STAGE_FILTER_PREFIX.length))]),
    ]
    const bad = stageValues.filter((s) => !(STAGES as readonly string[]).includes(s))
    if (bad.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['stage'], message: `Unknown stage: ${bad.join(', ')}` })
      return z.NEVER
    }
    const { status: _s, stage: _g, ...rest } = q
    return {
      ...rest,
      ...(status && status.length > 0 ? { status } : {}),
      ...(stageValues.length > 0 ? { stage: stageValues as Stage[] } : {}),
    }
  })
export type QueueQuery = z.output<typeof queueQuerySchema>

/** The filters only (what the tiles, count line and Clear filters look at). */
export type QueueFilters = Omit<QueueQuery, 'sort' | 'dir' | 'page' | 'pageSize'>

/** The multi-value filters (`?key=a,b`). */
export const QUEUE_LIST_FILTERS = [
  'status',
  'stage',
  'category',
  'priority',
  'raisedBy',
  'assignee',
] as const
export type QueueListFilter = (typeof QUEUE_LIST_FILTERS)[number]

/** True when any filter differs from the default (drives "match your filters" and Clear). */
export function hasActiveFilters(q: Partial<QueueFilters>): boolean {
  return !!(
    q.q ||
    q.createdFrom ||
    q.createdTo ||
    QUEUE_LIST_FILTERS.some((k) => (q[k]?.length ?? 0) > 0) ||
    q.overdue
  )
}

export const queueTicketSchema = ticketSchema.extend({
  publicCommentCount: z.number().int(),
  /** Null for callers who can't see internal notes (viewers). */
  internalNoteCount: z.number().int().nullable(),
  /** Attachments arrive in Wave 6. */
  attachmentCount: z.number().int(),
})
export type QueueTicket = z.infer<typeof queueTicketSchema>

export const queueTilesSchema = z.object({
  new: z.number().int(),
  in_progress: z.number().int(),
  waiting: z.number().int(),
  overdue: z.number().int(),
  done: z.number().int(),
})
export type QueueTiles = z.infer<typeof queueTilesSchema>

/** `GET /api/tickets` */
export const queueResponseSchema = z.object({
  tickets: z.array(queueTicketSchema),
  /** Tickets matching every filter. */
  total: z.number().int(),
  /** Every ticket the caller can see, ignoring filters ("12 of 24"). */
  overall: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  /** Counts with every filter except status, stage and overdue (PRD "Summary tiles"). */
  tiles: queueTilesSchema,
  /** Confidential tickets the caller can't see (all of them, ignoring filters; 0 for leads). */
  hiddenConfidential: z.number().int(),
  people: z.record(z.string(), personRefSchema),
})
export type QueueResponse = z.infer<typeof queueResponseSchema>

/** `GET /api/tickets/directory`: active people for filters and pickers, plus the org zone. */
export const directoryResponseSchema = z.object({
  people: z.array(personRefSchema),
  /** IANA zone the server reads dates in (created filters, "6 pm that day" ETAs). */
  timeZone: z.string(),
})
export type DirectoryResponse = z.infer<typeof directoryResponseSchema>

/** ETA value for "Reset to SLA". */
export const ETA_SLA = 'sla'

/**
 * `PATCH /api/tickets/:key` (HR). Every present field is applied in one transaction, in a fixed
 * order (category, priority, ETA, assignee, confidential, status), with one activity entry per
 * actual change. Moving into a Waiting status needs `waitingMessage` (posted as a public reply).
 */
export const ticketPatchSchema = z
  .object({
    version: z.number().int().nonnegative(),
    statusId: z.string().max(64).optional(),
    waitingMessage: z.string().max(10_000).optional(),
    assignee: z.string().max(64).nullable().optional(),
    priorityId: z.string().max(64).optional(),
    categoryId: z.string().max(64).optional(),
    /** `YYYY-MM-DD` (6 pm that day, org zone) or `sla`. */
    eta: z
      .string()
      .refine((v) => v === ETA_SLA || parseIsoDate(v) !== null, 'Use YYYY-MM-DD or "sla"')
      .optional(),
    confidential: z.boolean().optional(),
  })
  .strict()
export type TicketPatch = z.input<typeof ticketPatchSchema>

/** `PATCH /api/tickets/:key` → the ticket view, or `lostAccess` when the change hid it. */
export const ticketPatchResponseSchema = ticketDetailSchema.extend({
  /** The caller can no longer see the ticket (e.g. reassigned a confidential ticket away). */
  lostAccess: z.boolean().optional(),
})
export type TicketPatchResponse = z.infer<typeof ticketPatchResponseSchema>

export const WAITING_MESSAGE_REQUIRED = 'Tell the employee what you need so they can reply.'

// ---------------------------------------------------------------------------------------------
// Created-date presets (PRD "Filters")

export const DATE_PRESETS = ['any', 'today', '7d', '30d', 'month', 'custom'] as const
export type DatePreset = (typeof DATE_PRESETS)[number]

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  any: 'Any date',
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  month: 'This month',
  custom: 'Custom range',
}

function addDays(iso: string, days: number): string {
  const d = parseIsoDate(iso)
  if (!d) return iso
  const t = Date.UTC(d.year, d.month - 1, d.day) + days * 86_400_000
  return toIsoDate(t, 'UTC')
}

/** From/To dates for a preset (inclusive, in `timeZone`); empty for any/custom. */
export function presetRange(
  preset: DatePreset,
  now: number,
  timeZone?: string,
): { from: string; to: string } | null {
  const today = toIsoDate(now, timeZone)
  switch (preset) {
    case 'today':
      return { from: today, to: today }
    case '7d':
      return { from: addDays(today, -6), to: today }
    case '30d':
      return { from: addDays(today, -29), to: today }
    case 'month': {
      const p = zonedParts(now, timeZone)
      return { from: `${p.year}-${String(p.month).padStart(2, '0')}-01`, to: today }
    }
    default:
      return null
  }
}

/** Which preset a From/To pair corresponds to (custom when it matches none). */
export function presetFor(
  from: string | undefined,
  to: string | undefined,
  now: number,
  timeZone?: string,
): DatePreset {
  if (!from && !to) return 'any'
  for (const p of ['today', '7d', '30d', 'month'] as const) {
    const r = presetRange(p, now, timeZone)
    if (r && r.from === from && r.to === to) return p
  }
  return 'custom'
}
